import { useEffect, useState } from "react";
import { AdminLayout } from "../../components/Layouts";
import { api, readUser, fmt } from "../../lib/api";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

function fmtDay(d) {
  const dt = new Date(d);
  return dt.toLocaleDateString("tr-TR", { month: "short", day: "numeric" });
}

export default function AdminDashboard() {
  const user = readUser();
  const [data, setData] = useState({});
  const [latest, setLatest] = useState([]);
  const [chart, setChart] = useState([]);
  const [range, setRange] = useState(14);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/transactions").then((r) => setLatest((r.data || []).slice(0, 10))).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/admin/charts", { params: { days: range } }).then((r) => setChart(r.data || [])).catch(() => {});
  }, [range]);

  const stats = [
    ["Toplam Kullanıcı", data.users, "+kayıt"],
    ["Aktif Kullanıcı", data.active_users, "Yayında"],
    ["Aktif Paket", data.active_user_packages, "Çalışıyor"],
    ["Toplam Depozito", `$${fmt(data.total_deposits)}`, "Onaylı"],
    ["Toplam Çekim", `$${fmt(data.total_withdrawals)}`, "Tamamlanan"],
    ["Toplam Üretim", `$${fmt(data.total_production)}`, "Günlük toplam"],
    ["Toplam Referans", `$${fmt(data.total_referral)}`, "Komisyon"],
    ["Bekleyen Depozito", data.pending_deposits, "İnceleme"],
    ["Bekleyen Çekim", data.pending_withdrawals, "İşlem gerekli"],
  ];

  const chartData = chart.map((d) => ({ ...d, label: fmtDay(d.date) }));
  const ranges = [7, 14, 30, 60, 90];

  const tooltipStyle = { background: "#0b1823", border: "1px solid #0b1823", color: "#fff", fontSize: 11, padding: "6px 10px" };
  const axisStyle = { fontSize: 10, fill: "#8397a4" };

  return (
    <AdminLayout user={user} active="Yönetim Paneli">
      <div className="admin-stats five">
        {stats.map((x, i) => (
          <div className="admin-stat" data-testid={`admin-stat-${i}`} key={x[0]}>
            <span>{x[0]}</span>
            <strong>{x[1] ?? 0}</strong>
            <small>{x[2]}</small>
          </div>
        ))}
      </div>

      <section className="admin-panel">
        <div className="admin-panel-title">
          <div><span className="eyebrow">TREND GRAFİKLERİ</span><h2>Son {range} gün</h2></div>
          <div className="filter-pills" data-testid="chart-range-pills">
            {ranges.map((r) => (
              <button key={r} className={range === r ? "pill active" : "pill"} data-testid={`chart-range-${r}`} onClick={() => setRange(r)}>
                {r}g
              </button>
            ))}
          </div>
        </div>
        <div className="chart-grid">
          <div className="chart-card" data-testid="chart-deposits">
            <div className="chart-head"><span className="eyebrow">GÜNLÜK DEPOZİTO</span><strong>${fmt(chartData.reduce((s, d) => s + d.deposits, 0))}</strong></div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradDep" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#5dd5f3" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#5dd5f3" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#eef2f4" vertical={false} />
                <XAxis dataKey="label" tick={axisStyle} tickLine={false} axisLine={false} />
                <YAxis tick={axisStyle} tickLine={false} axisLine={false} width={44} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => `$${fmt(v)}`} />
                <Area type="monotone" dataKey="deposits" stroke="#1aa0c4" strokeWidth={2} fill="url(#gradDep)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card" data-testid="chart-withdrawals">
            <div className="chart-head"><span className="eyebrow">GÜNLÜK ÇEKİM</span><strong>${fmt(chartData.reduce((s, d) => s + d.withdrawals, 0))}</strong></div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#eef2f4" vertical={false} />
                <XAxis dataKey="label" tick={axisStyle} tickLine={false} axisLine={false} />
                <YAxis tick={axisStyle} tickLine={false} axisLine={false} width={44} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => `$${fmt(v)}`} />
                <Bar dataKey="withdrawals" fill="#e4a93a" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card" data-testid="chart-new-users">
            <div className="chart-head"><span className="eyebrow">YENİ KULLANICI</span><strong>{chartData.reduce((s, d) => s + d.new_users, 0)}</strong></div>
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#eef2f4" vertical={false} />
                <XAxis dataKey="label" tick={axisStyle} tickLine={false} axisLine={false} />
                <YAxis tick={axisStyle} tickLine={false} axisLine={false} width={34} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="new_users" stroke="#1a8b5e" strokeWidth={2} dot={{ r: 3, fill: "#1a8b5e" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-card" data-testid="chart-production">
            <div className="chart-head"><span className="eyebrow">GÜNLÜK ÜRETİM</span><strong>${fmt(chartData.reduce((s, d) => s + d.production, 0))}</strong></div>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradProd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1a8b5e" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#1a8b5e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#eef2f4" vertical={false} />
                <XAxis dataKey="label" tick={axisStyle} tickLine={false} axisLine={false} />
                <YAxis tick={axisStyle} tickLine={false} axisLine={false} width={44} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => `$${fmt(v)}`} />
                <Area type="monotone" dataKey="production" stroke="#1a8b5e" strokeWidth={2} fill="url(#gradProd)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="admin-panel">
        <div className="admin-panel-title">
          <div><span className="eyebrow">SON HAREKETLER</span><h2>İşlem defterinden en son 10</h2></div>
          <Link to="/admin/islem-defteri" className="btn btn-light" data-testid="admin-dash-ledger-link">Tüm hareketler</Link>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Tarih</th><th>Kullanıcı</th><th>Tür</th><th>Tutar</th><th>Bakiye Önce</th><th>Bakiye Sonra</th></tr></thead>
            <tbody>
              {latest.map((t, i) => (
                <tr key={i} data-testid={`admin-latest-${i}`}>
                  <td>{new Date(t.date).toLocaleString("tr-TR")}</td>
                  <td className="hash">{(t.user_id || "").slice(0, 10)}…</td>
                  <td>{t.type}</td>
                  <td className={t.amount < 0 ? "neg" : "pos"}>${fmt(t.amount)}</td>
                  <td>${fmt(t.balance_before)}</td>
                  <td>${fmt(t.balance_after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!latest.length && <div className="table-empty">Henüz işlem yok.</div>}
        </div>
      </section>
    </AdminLayout>
  );
}
